/**
 * Searchspring bundle ("boost your results") — Horizon-native FBT UI.
 * Fetches Searchspring p13n Recommend API and renders selectable product cards
 * with variant selects, live subtotal, and Add all.
 */

import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { refreshReviewsIoRatingSnippets } from '@theme/reviews-io-ratings';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const SS_P13N_RECOMMEND_ENDPOINT = 'https://{siteId}.a.p13n.searchspring.io/v1/recommend';
const SS_VIEWED_PRODUCTS_COOKIE = 'ssViewedProducts';
const SS_CART_PRODUCTS_COOKIE = 'ssCartProducts';
const SS_LAST_VIEWED_LIMIT = 10;

const CARD_SELECTOR = '.ss-recommendations__card';
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const LAZY_ROOT_MARGIN = '0px 0px 300px 0px';
const NAV_OVERFLOW_TOLERANCE_PX = 8;
const SNAP_ALIGN_THRESHOLD_PX = 2;
const STATUS_HIDE_MS = 2500;
const SOURCE_ID = 'ss-recommendations';

const SELECT_CARET_SVG = `
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {object} SizeOption
 * @property {string} label
 * @property {string} variantId
 * @property {boolean} available
 * @property {number} priceCents
 */

/**
 * @typedef {object} RecProduct
 * @property {string} id
 * @property {string} title
 * @property {string} url
 * @property {string} imageUrl
 * @property {string} handle
 * @property {number} rating
 * @property {number} ratingCount
 * @property {boolean} isBestSeller
 * @property {boolean} available
 * @property {SizeOption[]} sizes
 * @property {boolean} selected
 * @property {string} selectedVariantId
 */

/**
 * @typedef {object} SsRecommendationsRefs
 * @property {HTMLElement} [inner]
 * @property {HTMLElement} [nav]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLElement} [seed]
 * @property {HTMLElement} [plus]
 * @property {HTMLElement} [viewport]
 * @property {HTMLElement} [track]
 * @property {HTMLElement} [summary]
 * @property {HTMLElement} [summaryCount]
 * @property {HTMLElement} [summaryPrice]
 * @property {HTMLButtonElement} [addAllButton]
 * @property {HTMLElement} [statusMessage]
 * @property {HTMLElement} [emptyState]
 */

/* -------------------------------------------------------------------------- */
/* Pure helpers (module scope — no DOM / instance state)                      */
/* -------------------------------------------------------------------------- */

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttr(value) {
  return escapeHtml(value).replace(/'/g, '&#39;');
}

/**
 * @param {unknown} value
 * @returns {unknown}
 */
function parseJsonField(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * Searchspring prices are major currency units.
 * @param {unknown} value
 * @returns {number}
 */
function dollarsToCents(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Math.round(value * 100);
  const parsed = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

/**
 * @param {string | null | undefined} value
 * @returns {string[]}
 */
function parseSkuList(value) {
  if (!value) return [];
  return decodeURIComponent(String(value))
    .split(',')
    .map((sku) => sku.trim())
    .filter(Boolean);
}

/**
 * @param {string[]} values
 * @param {number} [limit]
 * @returns {string[]}
 */
function uniqueSkus(values, limit = Infinity) {
  return [...new Set(values.filter(Boolean))].slice(0, limit);
}

/**
 * @param {string} name
 * @returns {string | null}
 */
function getCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return match ? match.slice(prefix.length) : null;
}

/**
 * @param {unknown} item
 * @returns {string}
 */
function skuFromUnknown(item) {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const record = /** @type {{ sku?: unknown, uid?: unknown }} */ (item);
    return String(record.sku || record.uid || '');
  }
  return '';
}

/**
 * @param {Element} el
 * @returns {el is HTMLElement}
 */
function isHTMLElement(el) {
  return el instanceof HTMLElement;
}

/** Longest suffixes first — keep in sync with snippets/format-variant-size-label.liquid */
const SIZE_UNIT_REGISTRY = [
  ['floz', 'oz'],
  ['ml', 'ml'],
  ['oz', 'oz'],
  ['kg', 'kg'],
  ['lbs', 'lb'],
  ['lb', 'lb'],
  ['ct', 'ct'],
  ['pack', 'pack'],
  ['pk', 'pk'],
  ['g', 'g'],
];

/**
 * Formats a PLP size label for display only (e.g. 150ML → 150 ml).
 * @param {string} label
 * @returns {string}
 */
function formatVariantSizeLabel(label) {
  const raw = String(label || '').trim();
  if (!raw) return '';

  let normalized = raw.toLowerCase();
  normalized = normalized
    .replace(/fluid\s+ounces?/g, 'floz')
    .replace(/fl\.?\s*oz\.?/g, 'floz')
    .replace(/\s+/g, '');

  for (const [suffix, display] of SIZE_UNIT_REGISTRY) {
    if (!normalized.endsWith(suffix)) continue;

    const numberPart = normalized.slice(0, -suffix.length);
    if (!numberPart || normalized !== `${numberPart}${suffix}`) continue;

    return `${numberPart} ${display}`;
  }

  return raw.toLowerCase();
}

/**
 * @param {any} size
 * @returns {SizeOption | null}
 */
function mapSsSize(size) {
  const label = formatVariantSizeLabel(String(size?.label || size?.title || '').trim());
  const variantId = String(size?.variant_id || size?.variantId || '');
  if (!label || !variantId) return null;
  return {
    label,
    variantId,
    available: Number(size.available) > 0 || size.available === true,
    priceCents: dollarsToCents(size.price),
  };
}

/**
 * @param {any} variant
 * @returns {SizeOption | null}
 */
function mapVariantSize(variant) {
  const available =
    variant?.available === true ||
    variant?.attributes?.available === true ||
    Number(variant?.inventory_quantity) > 0 ||
    Number(variant?.attributes?.quantity) > 0 ||
    Number(variant?.mappings?.core?.available) > 0;

  const label = formatVariantSizeLabel(
    String(
      variant?.title ||
        variant?.option1 ||
        variant?.attributes?.title ||
        variant?.options?.size?.label ||
        variant?.mappings?.core?.sku ||
        ''
    ).trim()
  );

  const variantId = String(
    variant?.id ||
      variant?.legacyResourceId ||
      variant?.mappings?.core?.uid ||
      variant?.attributes?.uid ||
      ''
  );

  if (!label || !variantId) return null;

  const price = variant?.price || variant?.mappings?.core?.price || variant?.attributes?.price;

  return {
    label,
    variantId,
    available: Boolean(available),
    priceCents: dollarsToCents(price),
  };
}

/**
 * @param {any} result
 * @returns {SizeOption[]}
 */
function extractSizes(result) {
  const attrs = result?.attributes || {};
  const rawSizes = parseJsonField(attrs.ss_sizes);

  if (Array.isArray(rawSizes) && rawSizes.length) {
    return /** @type {SizeOption[]} */ (rawSizes.map(mapSsSize).filter(Boolean));
  }

  const variants = parseJsonField(attrs.variants) || parseJsonField(attrs.ss_variants) || [];
  if (!Array.isArray(variants)) return [];

  return /** @type {SizeOption[]} */ (variants.map(mapVariantSize).filter(Boolean));
}

/**
 * @param {any} result
 * @returns {RecProduct | null}
 */
function normalizeResult(result) {
  if (!result) return null;

  const core = result.mappings?.core || {};
  const attrs = result.attributes || {};
  const id = String(result.id || attrs.ss_id || '');
  if (!id) return null;

  const sizes = extractSizes(result);
  if (!sizes.length) return null;

  const firstAvailable = sizes.find((size) => size.available) || sizes[0];
  const url = String(core.url || '');
  const handleMatch = url.match(/\/products\/([^/?#]+)/);

  return {
    id,
    title: String(core.name || '').trim(),
    url,
    imageUrl: String(core.imageUrl || core.thumbnailImageUrl || ''),
    handle: handleMatch?.[1] || String(attrs.handle || ''),
    rating: Number(core.rating) || 0,
    ratingCount: Number(core.ratingCount) || 0,
    isBestSeller: Boolean(Number(attrs.ss_badge_bestseller) || attrs.ss_badge_bestseller === true),
    available: sizes.some((size) => size.available),
    sizes,
    selected: Boolean(firstAvailable?.available),
    selectedVariantId: firstAvailable?.variantId || sizes[0].variantId,
  };
}

/**
 * @param {RecProduct} product
 * @returns {SizeOption | undefined}
 */
function getSelectedSize(product) {
  return product.sizes.find((size) => size.variantId === product.selectedVariantId) || product.sizes[0];
}

/**
 * @returns {string[]}
 */
function readTrackerLastViewed() {
  try {
    const tracked =
      window.searchspring?.tracker?.getContext?.()?.shopper?.lastViewed ||
      window.searchspring?.context?.lastViewed ||
      null;
    if (!Array.isArray(tracked)) return [];
    return tracked.map(skuFromUnknown).filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * @returns {string[]}
 */
function readSkuListFromStorage() {
  try {
    const raw =
      window.localStorage?.getItem(SS_VIEWED_PRODUCTS_COOKIE) ||
      window.localStorage?.getItem('viewedProducts');
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : parsed?.value;
    if (!Array.isArray(list)) return parseSkuList(raw);

    return list.map(skuFromUnknown).filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Keep ssViewedProducts in sync when SnapUI is not present (Dev Path C).
 * @param {string} sku
 */
function persistViewedSku(sku) {
  if (!sku || typeof document === 'undefined') return;

  const existing = parseSkuList(getCookie(SS_VIEWED_PRODUCTS_COOKIE));
  const next = uniqueSkus([sku, ...existing.filter((value) => value !== sku)], SS_LAST_VIEWED_LIMIT);
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${SS_VIEWED_PRODUCTS_COOKIE}=${encodeURIComponent(next.join(','))}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
}

/**
 * @param {HTMLElement} viewport
 * @param {HTMLElement[]} cards
 * @returns {number}
 */
function getNearestCardIndex(viewport, cards) {
  const viewportLeft = viewport.getBoundingClientRect().left;
  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < cards.length; index += 1) {
    const card = cards[index];
    const distance = Math.abs(card.getBoundingClientRect().left - viewportLeft);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  }

  return nearestIndex;
}

/**
 * @param {HTMLElement} viewport
 * @param {HTMLElement} card
 * @param {ScrollBehavior} [behavior]
 */
function scrollCardIntoView(viewport, card, behavior = 'smooth') {
  const delta = card.getBoundingClientRect().left - viewport.getBoundingClientRect().left;
  const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  const targetLeft = Math.max(0, Math.min(maxScroll, viewport.scrollLeft + delta));
  viewport.scrollTo({ left: targetLeft, behavior });
}

/**
 * @returns {string[]}
 */
function collectCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  }
  return sectionIds;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * @extends {Component<SsRecommendationsRefs>}
 */
class SsRecommendations extends Component {
  /** @type {RecProduct[]} */
  #products = [];

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #loaded = false;

  /** @type {boolean} */
  #adding = false;

  requiredRefs = ['seed', 'track', 'addAllButton'];

  /* ---- Lifecycle --------------------------------------------------------- */

  connectedCallback() {
    super.connectedCallback();
    this.#observeLazyLoad();
    this.#bindUi();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#fetchController?.abort();
    this.#unbindUi();
  }

  /**
   * Called by Horizon section re-render / Component base.
   */
  updatedCallback() {
    super.updatedCallback();
    if (!this.#loaded) {
      this.#loadRecommendations();
    } else {
      this.#updateNavVisibility();
    }
  }

  #observeLazyLoad() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.#intersectionObserver?.disconnect();
        this.#loadRecommendations();
      },
      { rootMargin: LAZY_ROOT_MARGIN }
    );
    this.#intersectionObserver.observe(this);
  }

  #bindUi() {
    const { viewport, track, seed } = this.refs;

    if (viewport) {
      viewport.addEventListener('scroll', this.#onScroll, { passive: true });
      viewport.addEventListener('scrollend', this.#onScrollEnd, { passive: true });
      this.#resizeObserver = new ResizeObserver(() => this.#updateNavVisibility());
      this.#resizeObserver.observe(viewport);
    }

    track?.addEventListener('change', this.#onTrackChange);
    seed?.addEventListener('change', this.#onTrackChange);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  #unbindUi() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    const { viewport, track, seed } = this.refs;
    viewport?.removeEventListener('scroll', this.#onScroll);
    viewport?.removeEventListener('scrollend', this.#onScrollEnd);
    track?.removeEventListener('change', this.#onTrackChange);
    seed?.removeEventListener('change', this.#onTrackChange);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  /* ---- Event handlers ---------------------------------------------------- */

  #onSectionLoad = (/** @type {Event} */ event) => {
    const target = /** @type {CustomEvent & { detail?: { sectionId?: string } }} */ (event);
    if (target.detail?.sectionId && target.detail.sectionId !== this.dataset.sectionId) return;
    this.#loaded = false;
    this.#loadRecommendations();
  };

  #onScroll = () => {
    this.#updateNavButtons();
  };

  #onScrollEnd = () => {
    const { viewport } = this.refs;
    const cards = this.#getCarouselCards();
    if (!viewport || !cards.length) return;

    const index = getNearestCardIndex(viewport, cards);
    const card = cards[index];
    const delta = card.getBoundingClientRect().left - viewport.getBoundingClientRect().left;
    if (Math.abs(delta) > SNAP_ALIGN_THRESHOLD_PX) {
      scrollCardIntoView(viewport, card, 'auto');
    }
    this.#updateNavButtons();
  };

  #onTrackChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    if (target instanceof HTMLInputElement && target.dataset.action === 'toggle-select') {
      this.#handleToggleSelect(target);
      return;
    }

    if (target instanceof HTMLSelectElement && target.dataset.action === 'select-size') {
      this.#handleSelectSize(target);
    }
  };

  /**
   * @param {HTMLInputElement} input
   */
  #handleToggleSelect(input) {
    const product = this.#findProduct(input.dataset.productId);
    if (!product) return;

    product.selected = input.checked;
    input.closest(CARD_SELECTOR)?.classList.toggle('is-selected', product.selected);
    this.#updateSubtotal();
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #handleSelectSize(select) {
    const product = this.#findProduct(select.dataset.productId);
    if (!product || !select.value) return;

    product.selectedVariantId = select.value;
    const selectedSize = getSelectedSize(product);
    const priceEl = select.closest(CARD_SELECTOR)?.querySelector('[data-price]');
    if (priceEl && selectedSize) {
      priceEl.innerHTML = this.#formatProductPrice(product, selectedSize);
    }
    this.#updateSubtotal();
  }

  /* ---- Data loading ------------------------------------------------------ */

  async #loadRecommendations() {
    if (this.#loaded || this.#adding) return;

    const siteId = this.dataset.siteId?.trim();
    const profile = this.dataset.profile?.trim() || 'bundle';
    const seed = this.dataset.seed?.trim();
    const maxProducts = Number(this.dataset.maxProducts) || 4;

    if (!siteId || !seed) {
      this.#showEmpty(this.dataset.emptyLabel || '');
      return;
    }

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    // Match Live SnapUI payload (p13n + lastViewed from ssViewedProducts cookie)
    const url = SS_P13N_RECOMMEND_ENDPOINT.replace('{siteId}', siteId);
    const lastViewed = this.#getLastViewedSkus(seed);
    persistViewedSku(seed);

    /** @type {Record<string, unknown>} */
    const payload = {
      profiles: [{ tag: profile }],
      siteId,
      products: [seed],
      lastViewed,
      beacon: true,
    };

    const cartSkus = this.#getCartSkus();
    if (cartSkus.length) payload.cart = cartSkus;

    try {
      const response = await fetch(url, {
        method: 'POST',
        signal: this.#fetchController.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Recommend API ${response.status}`);

      const data = await response.json();
      this.#products = this.#parseRecommendResponse(data, maxProducts);
      this.#loaded = true;

      if (!this.#products.length) {
        this.#showEmpty(this.dataset.emptyLabel || '');
        return;
      }

      this.#render();
      this.#updateSubtotal();
      this.#updateNavVisibility();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      console.error('CHRIS::: SS recommendations load failed', error);
      this.#showEmpty(this.dataset.errorLabel || this.dataset.emptyLabel || '');
    }
  }

  /**
   * @param {any} data
   * @param {number} maxProducts
   * @returns {RecProduct[]}
   */
  #parseRecommendResponse(data, maxProducts) {
    const profileResult = Array.isArray(data) ? data[0] : data;
    const results = Array.isArray(profileResult?.results)
      ? profileResult.results
      : Array.isArray(data?.results)
        ? data.results
        : [];

    /** @type {RecProduct[]} */
    const products = [];
    for (const result of results) {
      const product = normalizeResult(result);
      if (product?.sizes.length) products.push(product);
      if (products.length >= maxProducts) break;
    }
    return products;
  }

  /**
   * Match Live Snap: lastViewed from ssViewedProducts cookie / tracker.
   * @param {string} seed
   * @returns {string[]}
   */
  #getLastViewedSkus(seed) {
    const viewed = [
      ...parseSkuList(getCookie(SS_VIEWED_PRODUCTS_COOKIE)),
      ...readSkuListFromStorage(),
      ...readTrackerLastViewed(),
    ];
    if (seed) viewed.unshift(seed);
    return uniqueSkus(viewed, SS_LAST_VIEWED_LIMIT);
  }

  /**
   * Cart SKUs from Liquid seed + Snap ssCartProducts cookie.
   * @returns {string[]}
   */
  #getCartSkus() {
    const fromLiquid = parseSkuList(this.dataset.cartSkus);
    const fromCookie = parseSkuList(getCookie(SS_CART_PRODUCTS_COOKIE));
    return uniqueSkus([...fromLiquid, ...fromCookie]);
  }

  /* ---- Rendering --------------------------------------------------------- */

  /**
   * @param {string} message
   */
  #showEmpty(message) {
    const { seed, track, plus, summary, emptyState, nav } = this.refs;
    if (seed) seed.innerHTML = '';
    if (track) track.innerHTML = '';
    if (plus) plus.hidden = true;
    if (summary) summary.hidden = true;
    if (nav) nav.hidden = true;
    if (emptyState) {
      emptyState.hidden = false;
      if (message) emptyState.textContent = message;
    }
  }

  #render() {
    const { seed, track, plus, summary, emptyState, viewport } = this.refs;
    if (!track || !seed) return;

    const scrollLeft = viewport?.scrollLeft || 0;

    if (emptyState) emptyState.hidden = true;
    if (summary) summary.hidden = false;

    // Live Snap: seed stays fixed; only recommendation cards scroll
    const [seedProduct, ...carouselProducts] = this.#products;
    const renderOptions = {
      showRatings: this.dataset.showRatings !== 'false',
      showBadges: this.dataset.showBadges !== 'false',
      bestSellerLabel: this.dataset.bestSellerLabel || 'Bestseller',
      badgeScheme: this.dataset.badgeScheme || 'scheme-1',
    };

    seed.innerHTML = seedProduct ? this.#renderCard(seedProduct, true, renderOptions) : '';
    track.innerHTML = carouselProducts
      .map((product) => this.#renderCard(product, false, renderOptions))
      .join('');

    if (plus) {
      plus.hidden = !(seedProduct && carouselProducts.length > 0);
    }

    this.style.setProperty('--ss-rec-carousel-count', String(Math.max(carouselProducts.length, 1)));

    if (viewport) {
      viewport.scrollLeft = scrollLeft;
    }

    if (renderOptions.showRatings) {
      this.#refreshReviewStars();
    }
  }

  /**
   * Same Reviews.io stars used on product cards / "customers also love".
   */
  async #refreshReviewStars() {
    await refreshReviewsIoRatingSnippets(this);
    requestAnimationFrame(() => {
      refreshReviewsIoRatingSnippets(this);
    });
  }

  /**
   * @param {RecProduct} product
   * @param {boolean} isSeed
   * @param {{ showRatings: boolean, showBadges: boolean, bestSellerLabel: string, badgeScheme: string }} options
   * @returns {string}
   */
  #renderCard(product, isSeed, options) {
    const selectedSize = getSelectedSize(product);
    const checked = product.selected && product.available;
    const cardClass = [
      'ss-recommendations__card',
      isSeed ? 'ss-recommendations__card--seed' : '',
      checked ? 'is-selected' : '',
      product.available ? '' : 'is-unavailable',
    ]
      .filter(Boolean)
      .join(' ');

    const badge =
      options.showBadges && product.isBestSeller
        ? `<div class="product-badges product-badges--top-right">
            <div class="product-badges__badge product-badges__badge--rectangle color-${escapeAttr(options.badgeScheme)}">
              ${escapeHtml(options.bestSellerLabel)}
            </div>
          </div>`
        : '';

    const rating =
      options.showRatings && product.handle
        ? `<div class="reviews-io-rating-snippet reviews-io-rating-snippet--center">
            <div class="ruk_rating_snippet" data-sku="${escapeAttr(product.handle)}" data-pending="true"></div>
          </div>`
        : '';

    const image = product.imageUrl
      ? `<img
          class="ss-recommendations__image"
          src="${escapeAttr(product.imageUrl)}"
          alt="${escapeAttr(product.title)}"
          width="400"
          height="400"
          loading="lazy"
          decoding="async"
        >`
      : `<span class="ss-recommendations__image ss-recommendations__image--placeholder"></span>`;

    return `
      <article
        class="${cardClass}"
        role="listitem"
        data-product-id="${escapeAttr(product.id)}"
        ${isSeed ? 'data-seed-card' : ''}
      >
        <div class="ss-recommendations__media">
          <label class="ss-recommendations__checkbox">
            <input
              type="checkbox"
              data-action="toggle-select"
              data-product-id="${escapeAttr(product.id)}"
              ${checked ? 'checked' : ''}
              ${product.available ? '' : 'disabled'}
              aria-label="${escapeAttr(product.title)}"
            >
            <span class="ss-recommendations__checkbox-ui" aria-hidden="true"></span>
          </label>
          ${badge}
          <a class="ss-recommendations__image-link" href="${escapeAttr(product.url)}">
            ${image}
          </a>
        </div>
        <div class="ss-recommendations__content">
          <h3 class="ss-recommendations__title">
            <a href="${escapeAttr(product.url)}">${escapeHtml(product.title)}</a>
          </h3>
          ${rating}
          <p class="ss-recommendations__price" data-price>${this.#formatProductPrice(product, selectedSize)}</p>
          <div class="ss-recommendations__sizes">
            ${this.#renderVariantSelector(product)}
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Mirrors product-card quick-shop variant UI (static label or select).
   * @param {RecProduct} product
   * @returns {string}
   */
  #renderVariantSelector(product) {
    const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    const selectedSize = getSelectedSize(product);

    if (product.sizes.length === 1) {
      const label = selectedSize?.available
        ? selectedSize.label
        : `${selectedSize?.label || ''} — ${soldOutLabel}`;
      return `
        <div class="quick-shop__select-wrapper quick-shop__select-wrapper--static product-card__variant-single">
          ${escapeHtml(label)}
        </div>
      `;
    }

    const options = product.sizes
      .map((size) => {
        const label = size.available ? size.label : `${size.label} — ${soldOutLabel}`;
        const selected = size.variantId === product.selectedVariantId ? 'selected' : '';
        return `
          <option
            value="${escapeAttr(size.variantId)}"
            data-available="${size.available ? 'true' : 'false'}"
            ${selected}
            ${size.available ? '' : 'disabled'}
          >
            ${escapeHtml(label)}
          </option>
        `;
      })
      .join('');

    const listOptions = product.sizes
      .map((size) => {
        const label = size.available ? size.label : `${size.label} — ${soldOutLabel}`;
        const isSelected = size.variantId === product.selectedVariantId;
        return `
          <li
            role="option"
            class="quick-shop__select-option${isSelected ? ' is-selected' : ''}"
            data-value="${escapeAttr(size.variantId)}"
            aria-selected="${isSelected ? 'true' : 'false'}"
            tabindex="-1"
          >
            ${escapeHtml(label)}
          </li>
        `;
      })
      .join('');

    const productId = escapeAttr(product.id);
    const productTitle = escapeAttr(product.title);
    const selectedLabel = selectedSize?.available
      ? selectedSize.label
      : `${selectedSize?.label || ''} — ${soldOutLabel}`;
    const listboxId = `ss-rec-variant-listbox-${productId}`;

    return `
      <div class="quick-shop__select-wrapper">
        <label class="visually-hidden" for="ss-rec-variant-${productId}">
          ${escapeHtml(product.title)}
        </label>
        <select
          id="ss-rec-variant-${productId}"
          class="quick-shop__select"
          data-action="select-size"
          data-product-id="${productId}"
          tabindex="-1"
          aria-hidden="true"
        >
          ${options}
        </select>
        <button
          type="button"
          class="quick-shop__select-trigger"
          aria-haspopup="listbox"
          aria-expanded="false"
          aria-controls="${listboxId}"
          aria-label="${productTitle}"
        >
          <span class="quick-shop__select-label">${escapeHtml(selectedLabel)}</span>
          <span class="svg-wrapper quick-shop__select-caret" aria-hidden="true">
            ${SELECT_CARET_SVG}
          </span>
        </button>
        <ul
          id="${listboxId}"
          class="quick-shop__select-options"
          role="listbox"
          hidden
        >
          ${listOptions}
        </ul>
      </div>
    `;
  }

  /* ---- Pricing / selection ----------------------------------------------- */

  /**
   * @param {string | undefined} productId
   * @returns {RecProduct | undefined}
   */
  #findProduct(productId) {
    if (!productId) return undefined;
    return this.#products.find((item) => item.id === productId);
  }

  /**
   * @returns {RecProduct[]}
   */
  #getSelectedProducts() {
    return this.#products.filter((product) => product.selected && product.available);
  }

  /**
   * @param {RecProduct} product
   * @param {SizeOption | undefined} selectedSize
   * @returns {string}
   */
  #formatProductPrice(product, selectedSize) {
    const prices = product.sizes.map((size) => size.priceCents).filter((cents) => cents > 0);
    const uniquePrices = [...new Set(prices)];
    const selectedCents = selectedSize?.priceCents || prices[0] || 0;
    const formatted = this.#formatCents(selectedCents);

    if (uniquePrices.length > 1) {
      const fromTemplate = this.dataset.fromPrice || 'from __PRICE__';
      const minFormatted = this.#formatCents(Math.min(...uniquePrices));
      return escapeHtml(fromTemplate.replace('__PRICE__', minFormatted));
    }

    return escapeHtml(formatted);
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatCents(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'AUD';
    return formatMoney(Math.max(0, cents), format, currency);
  }

  #updateSubtotal() {
    const { summaryCount, summaryPrice, addAllButton } = this.refs;
    const selected = this.#getSelectedProducts();
    const count = selected.length;
    const totalCents = selected.reduce((sum, product) => {
      const size = getSelectedSize(product);
      return sum + (size?.priceCents || 0);
    }, 0);

    if (summaryCount) {
      const template =
        count === 1
          ? this.dataset.subtotalOne || 'Subtotal for __COUNT__ item'
          : this.dataset.subtotalOther || 'Subtotal for __COUNT__ items';
      summaryCount.textContent = template.replace('__COUNT__', String(count));
    }

    if (summaryPrice) {
      summaryPrice.textContent = this.#formatCents(totalCents);
    }

    if (addAllButton) {
      addAllButton.disabled = count === 0 || this.#adding;
    }
  }

  /* ---- Carousel ---------------------------------------------------------- */

  /**
   * @returns {HTMLElement[]}
   */
  #getCarouselCards() {
    const { track } = this.refs;
    if (!track) return [];
    return [...track.querySelectorAll(CARD_SELECTOR)].filter(isHTMLElement);
  }

  /** Public — Theme `on:click="/scrollPrev"` */
  scrollPrev() {
    this.#scrollToAdjacentCard(-1);
  }

  /** Public — Theme `on:click="/scrollNext"` */
  scrollNext() {
    this.#scrollToAdjacentCard(1);
  }

  /**
   * @param {number} direction -1 | 1
   */
  #scrollToAdjacentCard(direction) {
    const { viewport } = this.refs;
    const cards = this.#getCarouselCards();
    if (!viewport || !cards.length) return;

    const currentIndex = getNearestCardIndex(viewport, cards);
    const nextIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + direction));

    if (nextIndex === currentIndex && direction !== 0) {
      scrollCardIntoView(viewport, cards[currentIndex]);
      return;
    }

    scrollCardIntoView(viewport, cards[nextIndex]);
  }

  #updateNavVisibility() {
    const { viewport, track, nav } = this.refs;
    if (!viewport || !track || !nav) return;

    const overflow = track.scrollWidth > viewport.clientWidth + NAV_OVERFLOW_TOLERANCE_PX;
    const isMobileCarousel = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    nav.hidden = !(isMobileCarousel && overflow);
    this.#updateNavButtons();
  }

  #updateNavButtons() {
    const { viewport, prevButton, nextButton } = this.refs;
    const cards = this.#getCarouselCards();

    if (!viewport || !cards.length) {
      if (prevButton) prevButton.disabled = true;
      if (nextButton) nextButton.disabled = true;
      return;
    }

    const index = getNearestCardIndex(viewport, cards);
    if (prevButton) prevButton.disabled = index <= 0;
    if (nextButton) nextButton.disabled = index >= cards.length - 1;
  }

  /* ---- Cart -------------------------------------------------------------- */

  /** Public — Theme `on:click="/addAllToCart"` */
  async addAllToCart() {
    if (this.#adding) return;

    const selected = this.#getSelectedProducts();
    if (!selected.length) return;

    const { addAllButton, statusMessage } = this.refs;
    const defaultLabel = this.dataset.addAllLabel || 'Add all to cart';
    const addingLabel = this.dataset.addingLabel || 'Adding…';
    const eventSourceId = this.id || SOURCE_ID;

    this.#adding = true;
    this.#updateSubtotal();
    this.#setAddAllBusy(addAllButton, addingLabel, true);
    this.#clearStatus(statusMessage);

    const payload = {
      items: selected.map((product) => ({
        id: Number(product.selectedVariantId),
        quantity: 1,
      })),
      sections: collectCartSectionIds().join(','),
    };

    const cartAddUrl =
      (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        this.#handleCartError(data, selected.length, eventSourceId, statusMessage);
        return;
      }

      await this.#handleCartSuccess(data, selected.length, eventSourceId, statusMessage);
    } catch (error) {
      console.error('CHRIS::: SS recommendations add-all failed', error);
      if (statusMessage) {
        statusMessage.hidden = false;
        statusMessage.textContent = this.dataset.errorLabel || 'Unable to add items.';
      }
    } finally {
      this.#adding = false;
      this.#setAddAllBusy(addAllButton, defaultLabel, false);
      this.#updateSubtotal();
    }
  }

  /**
   * @param {HTMLButtonElement | undefined} button
   * @param {string} label
   * @param {boolean} busy
   */
  #setAddAllBusy(button, label, busy) {
    if (!button) return;
    button.textContent = label;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {HTMLElement | undefined} statusMessage
   */
  #clearStatus(statusMessage) {
    if (!statusMessage) return;
    statusMessage.hidden = true;
    statusMessage.textContent = '';
  }

  /**
   * @param {any} data
   * @param {number} itemCount
   * @param {string} eventSourceId
   * @param {HTMLElement | undefined} statusMessage
   */
  #handleCartError(data, itemCount, eventSourceId, statusMessage) {
    this.dispatchEvent(
      new CartErrorEvent(eventSourceId, data.message || 'Error', data.description, data.errors)
    );

    if (statusMessage) {
      statusMessage.hidden = false;
      statusMessage.textContent = data.description || data.message || this.dataset.errorLabel || '';
    }

    this.dispatchEvent(
      new CartAddEvent({}, eventSourceId, {
        didError: true,
        source: SOURCE_ID,
        itemCount,
      })
    );
  }

  /**
   * @param {any} data
   * @param {number} itemCount
   * @param {string} eventSourceId
   * @param {HTMLElement | undefined} statusMessage
   */
  async #handleCartSuccess(data, itemCount, eventSourceId, statusMessage) {
    let cart = data;
    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartResponse.ok) {
        cart = await cartResponse.json();
      }
    } catch (cartError) {
      console.error('CHRIS::: SS recommendations cart refresh failed', cartError);
    }

    this.dispatchEvent(
      new CartAddEvent(cart, eventSourceId, {
        source: SOURCE_ID,
        itemCount,
        sections: data.sections,
      })
    );

    if (!statusMessage) return;

    statusMessage.hidden = false;
    statusMessage.textContent =
      typeof Theme !== 'undefined' && Theme.translations?.added
        ? Theme.translations.added
        : 'Added';

    window.setTimeout(() => {
      if (statusMessage) statusMessage.hidden = true;
    }, STATUS_HIDE_MS);
  }
}

if (!customElements.get('ss-recommendations')) {
  customElements.define('ss-recommendations', SsRecommendations);
}
